import { config } from "../config";
import { callGateway, type CallerIdentity } from "./gateway";
import type { LoginDetailsStatus } from "./subscriberCreate";
import type { SubscriberMatch } from "./subscriberSearch";

/**
 * Login details never pass through staff (owner 2026-10-18/19; radius-wiki
 * subscriber-cart-v1 §12.3). radaccess v1.125.
 *
 *   POST /access/operator/{ns}/subscribers/{AccountID}/login-details   subscriberlogin:write
 *     200 {login_details: "sent", to}          a new password was emailed
 *     202 {login_details: "confirm_sent", to}  the address is not confirmed: a confirm link went out
 *   POST /access/subscriber/email-confirm {token}   PUBLIC: the link in the email
 *
 * The password is generated and emailed by the service; no answer carries it.
 */
export interface LoginDetailsSent {
  login_details: LoginDetailsStatus;
  /** The address it went to, masked ("a***@example.in"). */
  to: string;
}

export async function sendLoginDetails(identity: CallerIdentity, ns: string, accountId: string): Promise<LoginDetailsSent> {
  const r = await callGateway<LoginDetailsSent>(
    identity,
    config().gatewayBase,
    `/operator/${encodeURIComponent(ns)}/subscribers/${encodeURIComponent(accountId)}/login-details`,
    { method: "POST", body: {} },
  );
  return { login_details: r.data?.login_details ?? "not_sent", to: r.data?.to ?? "" };
}

/** What the lookup card offers: a first send, or a reset once details went out. */
export function loginDetailsAction(m: SubscriberMatch): { label: string; disabled: boolean; why: string } {
  const label = m.login_details?.event === "sent" ? "Reset password" : "Send login details";
  if (!m.email) return { label, disabled: true, why: "Add an email in Edit profile first" };
  if (m.status === "disable") return { label, disabled: true, why: "This subscriber is disabled" };
  return { label, disabled: false, why: "" };
}

/** What the confirm dialog says will happen. */
export function loginDetailsPrompt(m: SubscriberMatch): string {
  if (!m.email_confirmed) {
    return `A confirm link goes to ${m.email}. When the subscriber opens it, a new password is emailed to them. Their current password keeps working until then.`;
  }
  return m.login_details?.event === "sent"
    ? `A new password is emailed to ${m.email}. The current password stops working once it is sent. Nobody here sees it.`
    : `The login details (a new password) are emailed to ${m.email}. Nobody here sees them.`;
}

/** The card's status line. */
export function loginDetailsStatusLine(m: SubscriberMatch): string {
  const d = m.login_details;
  const when = d?.at ? ` ${d.at.slice(0, 16)}` : "";
  switch (d?.event) {
    case "sent":
      return `Emailed${when}`;
    case "confirm_sent":
      return `Confirm link sent${when}; waiting for the subscriber to open it`;
    case "email_confirmed":
      return `Email confirmed${when}; login details not sent`;
    default:
      return m.email ? "Not sent yet" : "Not sent: no email";
  }
}

export type ConfirmOutcome =
  | { kind: "confirmed"; loginSent: boolean }
  | { kind: "expired" }
  | { kind: "unavailable" };

/** The confirm page's one call. No sign-in: the token is the credential. */
export async function confirmEmail(token: string, signal?: AbortSignal): Promise<ConfirmOutcome> {
  let res: Response;
  try {
    res = await fetch(`${config().gatewayBase}/subscriber/email-confirm`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ token }),
      ...(signal ? { signal } : {}),
    });
  } catch {
    return { kind: "unavailable" };
  }
  if (res.status === 410) return { kind: "expired" };
  if (res.status !== 200) return { kind: "unavailable" };
  const body = (await res.json().catch(() => ({}))) as { login_details?: unknown };
  return { kind: "confirmed", loginSent: body.login_details === "sent" };
}
